import { KrpanoInterface } from 'types/krpano'
import Krpano from 'utils/krpano'

export interface TargetImageAsset {
  id?: number | string
  tilesUrl?: string
  multires?: string
  s3Path?: string
  thumbnail?: string
}

export interface LoadSceneParams {
  krpano: KrpanoInterface | null | undefined
  pointId: string | number
  points?: any[]
  pointDetail?: any
  subImageId?: number | string | null
  targetH?: number
  targetV?: number
  targetFov?: number
  isTourVersion?: boolean
  directory?: string
  userList?: any[]
  currentUser?: { id?: number | string; role?: string } | null
  isDisplay?: string | null
  smoothSpeed?: string
}

/**
 * SceneNavigationHandler
 * Domain handler responsible for scene verification, dynamic XML injection,
 * transitioning Krpano scenes smoothly, re-rendering filtered annotations & direction arrows,
 * and positioning the camera view.
 */
export class SceneNavigationHandler {
  public static loadScene(params: LoadSceneParams): void {
    const {
      krpano,
      pointId,
      points = [],
      pointDetail,
      subImageId,
      targetH = 0,
      targetV = 0,
      targetFov = 100,
      isTourVersion = false,
      directory = 'rn',
      userList = [],
      currentUser = null,
      isDisplay = null,
      smoothSpeed = 'smooth(300,60,60)'
    } = params

    if (!krpano || typeof krpano.call !== 'function') return

    const pIdStr = String(pointId)
    const targetSubImgId = subImageId ? Number(subImageId) : undefined

    const targetPoint =
      (pointDetail && String(pointDetail.id) === pIdStr ? pointDetail : null) ||
      (points && points.find((pt: any) => String(pt.id) === pIdStr))

    let targetImageFile: TargetImageAsset | null = null
    if (targetSubImgId && targetPoint?.subImages) {
      const subImgs = Array.isArray(targetPoint.subImages)
        ? targetPoint.subImages
        : Object.values(targetPoint.subImages)
      targetImageFile =
        (subImgs.find(
          (img: any) => Number(img.id) === targetSubImgId
        ) as any) || null
    }
    if (!targetImageFile && targetPoint?.image360) {
      targetImageFile = targetPoint.image360
    }

    const sceneName = Krpano.getScenceNameByFileId(
      pIdStr,
      points,
      targetSubImgId
    )

    const currentScene = krpano.get('xml.scene')
    if (sceneName && currentScene !== sceneName && targetImageFile) {
      // 1. Generate & inject scene XML if missing
      if (krpano.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: targetImageFile.tilesUrl || undefined,
          multires: targetImageFile.multires || undefined,
          s3Path: targetImageFile.s3Path || '',
          thumbnailUrl: targetImageFile.thumbnail || ''
        })
        krpano.call(`loadxml(${screenString}, null, MERGE);`)
      }

      // 2. Transition scene
      krpano.call(`load_scene(${sceneName},${targetH},${targetV},${targetFov})`)

      // 3. Re-add filtered annotations for the active scene
      if (targetPoint?.annotations) {
        const annoList = targetPoint.annotations.filter((item: any) => {
          if (item.imageID != null) {
            return String(item.imageID) === String(targetImageFile?.id)
          }
          return (
            String(targetImageFile?.id) === String(targetPoint?.image360?.id)
          )
        })
        Krpano.AddAnnotation(
          krpano,
          annoList,
          isDisplay,
          isTourVersion,
          true,
          directory,
          userList,
          currentUser
        )
      }

      // 4. Re-add direction arrows with sub-image delta offset
      if (targetImageFile?.id) {
        const delta = Krpano.GetSubImageDelta(targetPoint, targetImageFile.id)
        if (targetPoint?.directions) {
          const directions = targetPoint.directions
            .map((dir: any) => {
              const order = points.find(
                (p: any) => String(p.id) !== pIdStr && dir.id.includes(p.id)
              )?.order
              return !order
                ? { ...dir, target: null }
                : { ...dir, target: order }
            })
            .filter((dir: any) => dir.target)
          Krpano.AddDirectionArrow(krpano, directions, delta)
        }
      }
    }

    // 5. Ensure status flag is true & position camera
    krpano.set('loadcomplete', 'true')
    krpano.call(`moveto(${targetH}, ${targetV}, ${smoothSpeed});`)
  }
}
